import { queryString } from '@helpers/utils/strings/strings';
import {
  authRoutes,
  basicOnlyRoutes,
  commonRoutes,
  nonBasicRoutes,
} from '@terminal/routes';
import { appPaths, authPaths } from '@terminal/routes/app-paths';
import { queryKeys } from '@terminal/routes/query-params';
import { AppRoute } from '@terminal/routes/Routes';
import { useAuthStore } from '@terminal/stores/use-auth/store/authStore';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { ServerError } from '@terminal/views/error';
import { matchRoutes, Navigate, RouteObject, useLocation } from 'react-router';

import { ErrorBoundaryWrapper } from '../error-boundary-wrapper/ErrorBoundaryWrapper';
import { AccountCheckView } from './account-check-view/AccountCheckView';

export const checkIsMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

export interface ProtectedRouteProps {
  config: AppRoute | RouteObject;
}

export const ProtectedView = ({ config }: ProtectedRouteProps) => {
  const location = useLocation();
  const isAuthenticated = useAuthStore(state => !!state.token);
  const isBasicUser = useSubscription(state => state.isBasicUser());
  const getFeatureFlagState = useFlags(state => state.getFeatureFlagState);

  const isAuthRoute = matchRoutes(authRoutes, location);
  const isNonBasicRoute = matchRoutes(nonBasicRoutes, location);
  const isBasicOnlyRoute = matchRoutes(basicOnlyRoutes, location);

  if (!isAuthenticated) {
    const isCommonRoute = matchRoutes(commonRoutes, location);

    if (isAuthRoute || isCommonRoute) {
      return config.element;
    }

    const isAccountSettings = location.pathname === appPaths.settings.account;

    if (isAccountSettings) {
      return null;
    }

    const loginRedirectTo = `${authPaths.login}${queryString({
      [queryKeys.redirect]: `${location.pathname}${location.search}`,
    })}`;

    return <Navigate to={loginRedirectTo} replace />;
  }

  if (isAuthRoute) {
    return <Navigate to={appPaths.alfred} replace />;
  }

  // Block legacy insight view for pro users
  if (!isBasicUser && isBasicOnlyRoute) {
    return <Navigate to={appPaths.base} replace />;
  }

  if (isBasicUser && isNonBasicRoute) {
    return <Navigate to={appPaths.base} replace />;
  }

  const routesConfig = config as AppRoute;
  const featureFlagDisabled =
    routesConfig.featureFlag && !getFeatureFlagState(routesConfig.featureFlag);

  if (featureFlagDisabled) {
    return <Navigate to={appPaths.base} replace />;
  }

  return (
    <ErrorBoundaryWrapper
      fallback={<ServerError />}
      resetKeys={[location.pathname, location.search]}
    >
      <AccountCheckView config={routesConfig} />
    </ErrorBoundaryWrapper>
  );
};
